import React from "react";
import { Dialog, Heading, Modal } from "react-aria-components";
import { t } from "ttag";

import type { SpecificationsBlock } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";

import { Themed } from "../../../styles/themes";
import { ButtonSecondary, Clickable } from "../../Clickables";
import { renderableWarrantyItems } from "../../SpecificationsBlock";
import { Svg } from "../../Svg";
import { useProductContext } from "../ProductContext/ProductContext";
import type { MergedProduct } from "../ProductContext/merge";
import type { SpecsFetchState } from "../ProductContext/useProductSpecs";
import { useProductSpecs } from "../ProductContext/useProductSpecs";
import type { SKU } from "../engine";

import styles from "./PDPDetailsModal.module.scss";

/**
 * The three spec panels, read from the reactivated `SpecificationsBlock` shape
 * the same way the PDP's own `SpecificationsBlock` component does.
 */
const SpecPanels = ({ specs }: { specs: SpecificationsBlock }) => {
    const features = specs.features?.items ?? [];
    const dimensions = specs.dimensions?.rows ?? [];
    const warranty = renderableWarrantyItems(specs.warranty?.items ?? []);
    return (
        <>
            {features.length > 0 && (
                <section>
                    <h3 className={styles.panelHeading}>{t`Features`}</h3>
                    <ul className={styles.features}>
                        {features.map((item, i) => (
                            <li key={i} className={styles.featureItem}>
                                <Svg
                                    aria-hidden="true"
                                    name="check"
                                    className={styles.check}
                                />
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
            <div className={styles.panelRow}>
                {dimensions.length > 0 && (
                    <section>
                        <h3 className={styles.panelHeading}>{t`Dimensions`}</h3>
                        <dl className={styles.dimensions}>
                            {dimensions.map((row, i) => (
                                <div key={i} className={styles.dimensionRow}>
                                    <dt>{row.label}</dt>
                                    <dd className={styles.dimensionValue}>
                                        {row.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </section>
                )}
                {warranty.length > 0 && (
                    <section>
                        <h3 className={styles.panelHeading}>{t`Warranty`}</h3>
                        <ul className={styles.warrantyList}>
                            {warranty.map(({ id, content }) => (
                                <li key={id}>{content}</li>
                            ))}
                        </ul>
                    </section>
                )}
            </div>
        </>
    );
};

type PDPDetailsModalViewProps = {
    /** Product to show; `null` keeps the modal closed. */
    product: MergedProduct | null;
    /** Resolved specs state, supplied by the connected wrapper (or a story). */
    specsState: SpecsFetchState;
    onClose: () => void;
    /** Commit the option into the suite — same dispatch as tapping its tile. */
    onAdd: () => void;
    /** CTA copy — "Add to Your Suite", or "Continue" for a grouping tile. */
    addLabel: string;
};

/** Presentational modal — all data in via props, so stories drive it directly. */
export const PDPDetailsModalView = ({
    product,
    specsState,
    onClose,
    onAdd,
    addLabel,
}: PDPDetailsModalViewProps) => {
    return (
        <Modal
            className={styles.modal}
            isOpen={product !== null}
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
            isDismissable
        >
            <Dialog className={styles.dialog}>
                {({ close }) =>
                    product && (
                        <>
                            <Clickable
                                className={styles.overlay}
                                onPress={close}
                                aria-hidden="true"
                                tabIndex={-1}
                            />
                            <Themed theme="white" className={styles.content}>
                                <Clickable
                                    className={styles.close}
                                    onPress={close}
                                >
                                    <Svg
                                        name="x"
                                        visuallyHiddenText={t`Close`}
                                    />
                                </Clickable>
                                <div className={styles.media}>
                                    <ImageChooserBlock
                                        value={product.image}
                                        attrs={{
                                            className: styles.mediaPicture,
                                        }}
                                        imageAttrs={{
                                            className: styles.mediaImage,
                                        }}
                                    />
                                </div>
                                <div className={styles.details}>
                                    <p className={styles.sku}>{product.sku}</p>
                                    <Heading
                                        slot="title"
                                        className={styles.title}
                                    >
                                        {product.name}
                                    </Heading>
                                    <p className={styles.price}>
                                        {product.msrp.formatted}
                                    </p>
                                    <ButtonSecondary
                                        className={styles.addButton}
                                        onPress={onAdd}
                                    >
                                        {addLabel}
                                    </ButtonSecondary>

                                    {/* Specs pop in once loaded. No loading or
                                        error zero-state: if they never arrive,
                                        the modal still earns its keep with the
                                        larger image and the PDP link. */}
                                    {specsState.status === "ready" &&
                                        specsState.specs && (
                                            <SpecPanels
                                                specs={specsState.specs}
                                            />
                                        )}

                                    {product.url && (
                                        <Clickable
                                            className={styles.fullDetailsLink}
                                            href={product.url}
                                            target="_blank"
                                        >
                                            {t`View Full Product Details`}
                                        </Clickable>
                                    )}
                                </div>
                            </Themed>
                        </>
                    )
                }
            </Dialog>
        </Modal>
    );
};

export type PDPDetailsModalProps = {
    /** SKU whose product details to show; `null` keeps the modal closed. */
    sku: SKU | null;
    onClose: () => void;
    /** Commit the option into the suite — same dispatch as tapping its tile. */
    onAdd: () => void;
    /** CTA copy — "Add to Your Suite", or "Continue" for a grouping tile. */
    addLabel: string;
};

/** Connected modal — resolves the SKU's product + specs and renders the view. */
export const PDPDetailsModal = ({
    sku,
    onClose,
    onAdd,
    addLabel,
}: PDPDetailsModalProps) => {
    const { getProduct, locale } = useProductContext();
    const product = sku ? getProduct(sku) : null;
    const specsState = useProductSpecs(sku, locale);
    return (
        <PDPDetailsModalView
            product={product}
            specsState={specsState}
            onClose={onClose}
            onAdd={onAdd}
            addLabel={addLabel}
        />
    );
};
